import React, { useEffect } from 'react';
import { AppBar, Box, Container, GlobalStyles, Toolbar, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { isExternalSafetyReviewer } from '../../auth/access';

type Props = {
  children: React.ReactNode;
};

/**
 * Minimal chrome for WorkSafeBC / external safety reviewers (Epic 160).
 * No warehouse drawer, scope editor, pipeline chips, or admin navigation.
 */
export default function ExternalSafetyReviewLayout({ children }: Props) {
  const { effectiveUser, logout } = useAuth();
  const reviewerLabel = isExternalSafetyReviewer(effectiveUser)
    ? 'External Safety Review'
    : 'Safety Scan Review';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Edgentik Signed Safety Scan';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <GlobalStyles
        styles={{
          '@media print': {
            '@page': {
              margin: '12mm',
              size: 'auto',
            },
            html: {
              height: 'auto',
            },
            body: {
              printColorAdjust: 'exact',
              WebkitPrintColorAdjust: 'exact',
            },
            'header, nav, .MuiAppBar-root, button, [data-print-hide="true"]': {
              display: 'none !important',
            },
            'a[href]::after, a[href]::before': {
              content: '"" !important',
            },
            '[data-print-hide="true"]': {
              display: 'none !important',
            },
          },
        }}
      />
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider', '@media print': { display: 'none' } }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Edgentik
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reviewerLabel}
          </Typography>
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={() => logout()}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              color: 'primary.main',
              font: 'inherit',
            }}
          >
            Sign out
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ py: 3 }}>
        {children}
      </Container>
    </Box>
  );
}
